import { Controller } from "@hotwired/stimulus";
import { getTouristCardRedirectUrl } from "./TouristCardRedirectUrl";

export default class TouristCardRedirectController extends Controller<HTMLElement> {
    static targets = ["overlay", "panel", "close", "continue"];

    declare readonly overlayTarget: HTMLElement;
    declare readonly panelTarget: HTMLElement;
    declare readonly closeTarget: HTMLButtonElement;
    declare readonly continueTarget: HTMLAnchorElement;

    private lastTrigger: HTMLAnchorElement | null = null;

    private readonly onKeyDown = (event: KeyboardEvent): void => {
        if (this.overlayTarget.hidden) return;

        if (event.key === "Escape") {
            event.preventDefault();
            this.close();
        } else if (event.key === "Tab") {
            this.keepFocusInside(event);
        }
    };

    private readonly onBeforeCache = (): void => this.close();

    connect(): void {
        document.addEventListener("keydown", this.onKeyDown);
        document.addEventListener("turbo:before-cache", this.onBeforeCache);
    }

    disconnect(): void {
        document.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("turbo:before-cache", this.onBeforeCache);
        this.close();
    }

    open(event: MouseEvent): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLAnchorElement)) return;

        const redirectUrl = getTouristCardRedirectUrl(trigger.href);
        if (!redirectUrl) return;

        event.preventDefault();
        this.lastTrigger = trigger;
        this.continueTarget.href = redirectUrl;
        this.overlayTarget.hidden = false;
        this.overlayTarget.setAttribute("aria-hidden", "false");
        document.body.classList.add("tourist-card-redirect-open");
        this.panelTarget.focus({ preventScroll: true });
    }

    closeFromAction(event: Event): void {
        event.preventDefault();
        this.close();
    }

    closeFromBackdrop(event: MouseEvent): void {
        if (event.target === this.overlayTarget) this.close();
    }

    continue(): void {
        this.close();
    }

    private close(): void {
        this.overlayTarget.hidden = true;
        this.overlayTarget.setAttribute("aria-hidden", "true");
        document.body.classList.remove("tourist-card-redirect-open");
        if (this.lastTrigger?.isConnected) this.lastTrigger.focus();
        this.lastTrigger = null;
    }

    private keepFocusInside(event: KeyboardEvent): void {
        const focusable = Array.from(this.panelTarget.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
        )).filter(element => !element.closest("[hidden]"));
        if (focusable.length === 0) {
            event.preventDefault();
            this.panelTarget.focus();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
}
